import { httpClient } from '@/api/httpClient';
import type {
  AreaFormValues,
  AreaOption,
  DispenserOption,
  PaginatedResponse,
  Product,
  StationComplianceProfile,
  StationComplianceProfileFormValues,
  StationFormValues,
  StationOption,
  TankAllowedProductsResponse,
  TankFormValues,
  TankOption
} from '../types/stationAssets';
import { stationAssetCache } from '../offline/stationAssetCache';

const normalizeList = <T>(data: T[] | PaginatedResponse<T>) =>
  Array.isArray(data) ? data : data.results;

const withStationQuery = (endpoint: string, stationId?: string | number | null) => {
  if (!stationId) {
    return endpoint;
  }

  const separator = endpoint.includes('?') ? '&' : '?';
  return `${endpoint}${separator}station=${encodeURIComponent(String(stationId))}`;
};

export const stationAssetsApi = {
  async listStations() {
    const data = await httpClient<StationOption[] | PaginatedResponse<StationOption>>(
      '/api/v1/stations/'
    );
    return normalizeList(data);
  },
  createStation(values: StationFormValues) {
    return httpClient<StationOption>('/api/v1/stations/', {
      method: 'POST',
      body: JSON.stringify(values)
    });
  },
  async listAreas(stationId?: string | number | null) {
    const data = await httpClient<AreaOption[] | PaginatedResponse<AreaOption>>(
      withStationQuery('/api/v1/areas/', stationId)
    );
    return normalizeList(data);
  },
  getArea(areaId: string | number) {
    return httpClient<AreaOption>(`/api/v1/areas/${areaId}/`);
  },
  createArea(values: AreaFormValues) {
    return httpClient<AreaOption>('/api/v1/areas/', {
      method: 'POST',
      body: JSON.stringify(values)
    });
  },
  updateArea(areaId: string | number, values: Partial<AreaFormValues>) {
    return httpClient<AreaOption>(`/api/v1/areas/${areaId}/`, {
      method: 'PATCH',
      body: JSON.stringify(values)
    });
  },
  async listTanks(stationId?: string | number | null) {
    const data = await httpClient<TankOption[] | PaginatedResponse<TankOption>>(
      withStationQuery('/api/v1/tanks/', stationId)
    );
    const tanks = normalizeList(data);
    void stationAssetCache.saveTanks(tanks);
    return tanks;
  },
  getTank(tankId: string | number) {
    return httpClient<TankOption>(`/api/v1/tanks/${tankId}/`);
  },
  async listProducts() {
    const data = await httpClient<Product[] | PaginatedResponse<Product>>('/api/v1/products/');
    const products = normalizeList(data);
    void stationAssetCache.saveProducts(products);
    return products;
  },
  async getTankAllowedProducts(stationId: string | number, tankId: string | number) {
    try {
      return await httpClient<TankAllowedProductsResponse>(
        `/api/v1/stations/${stationId}/tanks/${tankId}/allowed-products/`
      );
    } catch (error) {
      const allowedProducts = await stationAssetCache.getAllowedProductsForTank(tankId);

      if (allowedProducts.length > 0) {
        return {
          tank: {
            id: tankId,
            name: String(tankId),
            code: ''
          },
          allowed_products: allowedProducts
        };
      }

      throw error;
    }
  },
  createTank(values: TankFormValues) {
    return httpClient<TankOption>('/api/v1/tanks/', {
      method: 'POST',
      body: JSON.stringify(values)
    });
  },
  updateTank(tankId: string | number, values: Partial<TankFormValues>) {
    return httpClient<TankOption>(`/api/v1/tanks/${tankId}/`, {
      method: 'PATCH',
      body: JSON.stringify(values)
    });
  },
  async listDispensers(stationId?: string | number | null) {
    const data = await httpClient<DispenserOption[] | PaginatedResponse<DispenserOption>>(
      withStationQuery('/api/v1/asset-dispensers/', stationId)
    );
    return normalizeList(data);
  },
  getStationComplianceProfile(stationId: string | number) {
    return httpClient<StationComplianceProfile>(
      `/api/v1/stations/${stationId}/compliance-profile/`
    );
  },
  updateStationComplianceProfile(
    stationId: string | number,
    values: StationComplianceProfileFormValues
  ) {
    const logo = values.logo?.[0];
    const payload = {
      business_name: values.business_name,
      trade_name: values.trade_name,
      rfc: values.rfc.toUpperCase(),
      cre_permit_number: values.cre_permit_number,
      asea_registration_number: values.asea_registration_number,
      commercial_image: values.commercial_image,
      station_address: values.station_address,
      phone: values.phone,
      email: values.email,
      legal_representative: values.legal_representative,
      operations_manager: values.operations_manager,
      technical_responsible: values.technical_responsible,
      document_footer: values.document_footer,
      document_version: values.document_version || '1.0'
    };

    if (logo) {
      const formData = new FormData();

      Object.entries(payload).forEach(([key, value]) => {
        formData.append(key, value);
      });
      formData.append('logo', logo);

      return httpClient<StationComplianceProfile>(
        `/api/v1/stations/${stationId}/compliance-profile/`,
        {
          method: 'PUT',
          body: formData
        }
      );
    }

    return httpClient<StationComplianceProfile>(
      `/api/v1/stations/${stationId}/compliance-profile/`,
      {
        method: 'PUT',
        body: JSON.stringify(payload)
      }
    );
  }
};
